import Raven from 'raven-js';
import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { uploadFile } from '../../draft-transplant/api/FileManagerApi';
import { getSignedSrc } from '../../draft-transplant/utils/Images';
import { HIDDEN_IN_APP_PRIVATE_NOT_INDEXABLE } from '../../draft-transplant/utils/constants';
import { EditorConfigContext } from '../contexts/EditorConfigContext';
import { INLINE_IMAGE } from '../editorCapabilities/constants';

interface IProps {
  updateImage: (data: any) => void;
  attrs: MutableRefObject<any>;
}

export function useImageUploadHandler(props: IProps) {
  const { updateImage, attrs } = props;
  const editorConfig = useContext(EditorConfigContext);
  const preserveFileManagerId =
    editorConfig[INLINE_IMAGE]?.preserveFileManagerId ?? false;
  const customUploadImage = editorConfig[INLINE_IMAGE]?.uploadImage;
  const [progress, setProgress] = useState<any>(null);
  const [signedUrl, setSignedUrl] = useState<any>(null);
  const [fileManagerId, setFileManagerId] = useState<number | null>(null);

  useEffect(() => {
    if (signedUrl && attrs.current.src !== signedUrl) {
      updateImage({
        image: {
          src: signedUrl,
          isTemporary: false,
          uploadPercent: 100,
          fileToUpload: null,
          fileManagerId: preserveFileManagerId ? fileManagerId : null,
        },
      });
    } else if (progress && attrs.current.uploadPercent !== progress) {
      updateImage({
        image: {
          uploadPercent: progress,
        },
      });
    }
  }, [
    progress,
    signedUrl,
    fileManagerId,
    preserveFileManagerId,
    updateImage,
    attrs,
  ]);

  const onProgress = useCallback(
    (progressData: any) => {
      if (!progressData.lengthComputable) {
        return;
      }
      const percentComplete =
        parseInt(`${progressData.loaded / progressData.total}`, 10) * 100;
      setProgress(percentComplete);
    },
    [setProgress]
  );

  useEffect(() => {
    if (attrs.current.isTemporary && attrs.current.fileToUpload) {
      const uploadFn = customUploadImage || uploadFile;
      uploadFn(attrs.current.fileToUpload, {
        access:
          attrs.current.fileManagerAccess ||
          HIDDEN_IN_APP_PRIVATE_NOT_INDEXABLE,
        folderPath: attrs.current.folderPath,
        onProgress,
      })
        .then((data: any) => {
          const { id } = data.objects[0];
          const imageUrl = customUploadImage ? id : getSignedSrc(id);
          setFileManagerId(customUploadImage ? null : id);
          setSignedUrl(imageUrl);
        })
        .catch((error: any) => {
          console.warn('Failed to upload file');
          Raven.captureException(error);
        });
    }
  }, [setSignedUrl, onProgress, attrs, customUploadImage]);
}
